import { useState, useEffect, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Header from "../../../Components/adminDashboard/Header/Header";
import combinedServices from "../../../shared/services/user-service";
import Swal from "sweetalert2";
import html2pdf from "html2pdf.js";
import Accordion from "react-bootstrap/Accordion";
import Chart from "chart.js/auto";

const SalesReport = () => {
  const { storeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { storeName } = location.state || {};
  
  const [orderData, setOrderData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    fromDate: "",
    toDate: ""
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [limit, setLimit] = useState(1000);
  const [paymentStatus, setPaymentStatus] = useState("");
  const [orderStatus, setOrderStatus] = useState("");
  const [totalSales, setTotalSales] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [metrics, setMetrics] = useState({
    totalRevenue: 0,
    completedOrders: 0,
    pendingOrders: 0,
    averageOrderValue: 0,
    topProducts: [],
    paymentMethods: {},
    ordersByStatus: {},
    totalCustomers: 0,
    totalItems: 0,
    uniqueOrders: 0
  });

  const statusChartRef = useRef(null);
  const productsChartRef = useRef(null);
  const statusChartInstance = useRef(null);
  const productsChartInstance = useRef(null);

  const fetchOrderData = async (fromDate = null, toDate = null, search = searchQuery, limitValue = limit, paymentStat = paymentStatus, orderStat = orderStatus) => {
    setLoading(true);
    try {
      const response = await combinedServices.getallorders(
        1, // page
        limitValue, // limit
        search, // searchQuery
        paymentStat, // paymentStatus
        orderStat, // orderStatus
        storeId, // storeID
        fromDate, // startdate
        toDate // enddate
      );
      
      if (response.status === 200) {
        let orders = response.data.data || [];
        setOrderData(orders);
        const uniqueOrderIds = new Set(orders.map(order => order.order_id));
        const uniqueOrdersCount = uniqueOrderIds.size;
        const totalItemsCount = orders.length;
        setTotalOrders(uniqueOrdersCount);
        
        const totalRevenue = orders.reduce((sum, order) => 
          sum + parseFloat(order.price || (order.unit_price * order.quantity) || 0), 0
        );
        setTotalSales(totalRevenue);
        
        const completedOrderIds = new Set(
          orders.filter(order => order.paymentStatus === 'COMPLETED').map(order => order.order_id)
        );
        const completedOrders = completedOrderIds.size;
        
        const pendingOrderIds = new Set(
          orders.filter(order => order.orderStatus === 'Pending').map(order => order.order_id)
        );
        const pendingOrders = pendingOrderIds.size;
        
        const averageOrderValue = uniqueOrdersCount > 0 ? totalRevenue / uniqueOrdersCount : 0;
        const uniqueCustomers = new Set(orders.map(order => order.userId)).size;
        
        // Top products analysis
        const productSales = {};
        orders.forEach(order => {
          const productName = order.product_name || 'Unknown Product';
          const revenue = parseFloat(order.price || (order.unit_price * order.quantity) || 0);
          if (productSales[productName]) {
            productSales[productName].quantity += order.quantity;
            productSales[productName].revenue += revenue;
            productSales[productName].orders += 1;
          } else {
            productSales[productName] = {
              quantity: order.quantity,
              revenue: revenue,
              orders: 1
            };
          }
        });
        
        const topProducts = Object.entries(productSales)
          .map(([name, data]) => ({ name, ...data }))
          .sort((a, b) => b.revenue - a.revenue)
          .slice(0, 5);
        
        // Payment methods analysis
        const paymentMethods = {};
        orders.forEach(order => {
          const method = order.PayerID ? 'PayPal' : 'Other';
          paymentMethods[method] = (paymentMethods[method] || 0) + 1;
        });
        
        // Orders by payment status
        const ordersByStatus = {};
        orders.forEach(order => {
          let status = order.paymentStatus || 'Unknown';
          // Normalize status to handle case variations
          status = status.toUpperCase();
          ordersByStatus[status] = (ordersByStatus[status] || 0) + 1;
        });
        
        setMetrics({
          totalRevenue,
          completedOrders,
          pendingOrders,
          averageOrderValue,
          topProducts,
          paymentMethods,
          ordersByStatus,
          totalCustomers: uniqueCustomers,
          totalItems: totalItemsCount,
          uniqueOrders: uniqueOrdersCount
        });
        
        // Update charts after setting metrics
        setTimeout(() => {
          createCharts(ordersByStatus, topProducts);
        }, 100);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      Swal.fire("Error!", "Failed to fetch order data.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (storeId) {
      fetchOrderData();
    }
  }, [storeId]);

  const handleDateFilter = () => {
    if (!dateRange.fromDate || !dateRange.toDate) {
      Swal.fire("Error!", "Please select both from and to dates.", "error");
      return;
    }
    fetchOrderData(dateRange.fromDate, dateRange.toDate);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (storeId) {
        fetchOrderData(dateRange.fromDate, dateRange.toDate);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, limit, paymentStatus, orderStatus]);

  const clearFilter = () => {
    setDateRange({ fromDate: "", toDate: "" });
    fetchOrderData();
  };

  const createCharts = (ordersByStatus, topProducts) => {
    // Destroy existing charts
    if (statusChartInstance.current) {
      statusChartInstance.current.destroy();
    }
    if (productsChartInstance.current) {
      productsChartInstance.current.destroy();
    }

    // Payment Status Pie Chart
    if (statusChartRef.current && Object.keys(ordersByStatus).length > 0) {
      const ctx = statusChartRef.current.getContext('2d');
      
      // Map status to specific colors
      const statusColors = {
        'COMPLETED': '#28a745',
        'PENDING': '#ffc107', 
        'FAILED': '#dc3545',
        'CANCELLED': '#6c757d',
        'UNKNOWN': '#17a2b8'
      };
      
      const colors = Object.keys(ordersByStatus).map(status => 
        statusColors[status] || '#6f42c1'
      );
      
      statusChartInstance.current = new Chart(ctx, {
        type: 'pie',
        data: {
          labels: Object.keys(ordersByStatus),
          datasets: [{
            data: Object.values(ordersByStatus),
            backgroundColor: colors,
            borderWidth: 2,
            borderColor: '#fff'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom'
            },
            title: {
              display: true,
              text: 'Payment Status Distribution'
            }
          }
        }
      });
    }

    // Top Products Bar Chart
    if (productsChartRef.current && topProducts.length > 0) {
      const ctx = productsChartRef.current.getContext('2d');
      productsChartInstance.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: topProducts.map(p => p.name.length > 15 ? p.name.substring(0, 15) + '...' : p.name),
          datasets: [{
            label: 'Revenue ($)',
            data: topProducts.map(p => p.revenue),
            backgroundColor: '#007bff',
            borderColor: '#0056b3',
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: function(value) {
                  return '$' + value.toFixed(0);
                }
              }
            }
          },
          plugins: {
            legend: {
              display: false
            },
            title: {
              display: true,
              text: 'Top Products by Revenue'
            }
          }
        }
      });
    }
  };

  useEffect(() => {
    return () => {
      // Cleanup charts on unmount
      if (statusChartInstance.current) statusChartInstance.current.destroy();
      if (productsChartInstance.current) productsChartInstance.current.destroy();
    };
  }, []);

  const generateOrderPDF = (orderId, orders) => {
    const orderDate = new Date(orders[0]?.createdOn || orders[0]?.created_at || Date.now());
    
    // Find order with null product and get first image
    const nullProductOrder = orders.find(order => !order.product);
    const images = nullProductOrder?.image ? JSON.parse(nullProductOrder.image) : [];
    const watermarkImage = images[0] || null;
    
    const htmlContent = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>NAMBA 2025 Nationals Order Report</title>
          <style>
              body {
                  font-family: 'Courier New', monospace;
                  margin: 0;
                  padding: 0;
                  background-color: white;
                  color: black;
                  font-size: 11px;
                  line-height: 1.2;
              }
              
              .page {
                  width: 8.5in;
                  min-height: 11in;
                  margin: 0 auto;
                  padding: 0.5in;
                  box-sizing: border-box;
                  position: relative;
                  background-size: 400px 400px;
                  background-position: center;
                  background-repeat: no-repeat;
                  background-attachment: fixed;
              }
              
              .content {
                  position: relative;
                  z-index: 1;
              }
              
              .header {
                  text-align: center;
                  margin-bottom: 30px;
                  padding-bottom: 10px;
                  border-bottom: 1px solid #ccc;
                  position: relative;
                  z-index: 1;
              }
              
              .header h1 {
                  font-size: 20px;
                  font-weight: bold;
                  margin: 0 0 5px 0;
                  padding: 0;
                  letter-spacing: 0.5px;
              }
              
              .header h2 {
                  font-size: 16px;
                  font-weight: bold;
                  margin: 0;
                  padding: 0;
                  letter-spacing: 0.5px;
              }
              
              .customer-section {
                  margin-bottom: 25px;
              }
              
              .customer-name {
                  font-size: 14px;
                  font-weight: bold;
                  margin-bottom: 10px;
                  text-decoration: underline;
              }
              
              .info-table {
                  width: 100%;
                  border-collapse: collapse;
                  margin-bottom: 15px;
              }
              
              .info-table th,
              .info-table td {
                  padding: 4px 8px;
                  text-align: left;
                  vertical-align: top;
                  font-size: 11px;
              }
              
              .info-table th {
                  background-color: #f0f0f0;
                  font-weight: bold;
              }
              
              .order-block {
                  margin-bottom: 25px;
                  border: 1px solid #ccc;
                  padding: 10px;
                  position: relative;
              }
              
              .status-table {
                  width: 100%;
                  border-collapse: collapse;
                  margin-bottom: 10px;
              }
              
              .status-table th,
              .status-table td {
                  padding: 3px 6px;
                  text-align: left;
                  vertical-align: top;
                  font-size: 11px;
              }
              
              .status-table th {
                  background-color: #f5f5f5;
                  font-weight: bold;
              }
              
              .order-table {
                  width: 100%;
                  border-collapse: collapse;
                  margin-bottom: 10px;
              }
              
              .order-table th,
              .order-table td {
                  padding: 3px 6px;
                  text-align: left;
                  vertical-align: top;
                  font-size: 11px;
              }
              
              .order-table th {
                  background-color: #f5f5f5;
                  font-weight: bold;
              }
              
              .notes-section {
                  margin-top: 20px;
                  padding-top: 10px;
                  border-top: 1px solid #ccc;
              }
              
              .notes-label {
                  font-weight: bold;
                  margin-bottom: 5px;
              }
              
              .notes-content {
                  font-style: italic;
                  min-height: 40px;
              }
              
              .footer {
                  position: absolute;
                  bottom: 20px;
                  left: 0;
                  right: 0;
                  text-align: center;
                  font-size: 10px;
                  color: #666;
              }
              
              .spacer {
                  height: 15px;
              }
          </style>
      </head>
      <body>
          <div class="page" style="background-image: url('${watermarkImage}'); opacity: 1;">
              <div class="content">
              <div class="header">
                  <h1>NAMBA 2025 Nationals</h1>
                  <h2>Order Report</h2>
              </div>
              
              <div class="customer-section">
                  <div class="customer-name">Andrew St Amant</div>
                  
                  <table class="info-table">
                      <thead>
                          <tr>
                              <th>#</th>
                              <th>Email</th>
                              <th>Home Phone</th>
                              <th>Mobile Phone</th>
                          </tr>
                      </thead>
                      <tbody>
                          <tr>
                              <td>549</td>
                              <td>drewstamant@gmail.com</td>
                              <td></td>
                              <td>(850) 628-7715</td>
                          </tr>
                      </tbody>
                  </table>
                  
                  <div class="spacer"></div>
                  
                  <div class="order-block">            
                      <table class="status-table">
                          <thead>
                              <tr>
                                  <th>Order</th>
                                  <th>Order Date</th>
                                  <th>Order Status</th>
                                  <th>Payment Status</th>
                                  <th>Payment ID</th>
                              </tr>
                          </thead>
                          <tbody>
                              <tr>
                                  <td>${orderId || 'NO ID'}</td>
                                  <td>${orderDate.toLocaleDateString()}</td>
                                  <td>${orders[0]?.orderStatus || 'N/A'}</td>
                                  <td>${orders[0]?.paymentStatus || 'N/A'}</td>
                                  <td>${orders[0]?.payment_id || 'N/A'}</td>
                              </tr>
                          </tbody>
                      </table>
                      
                      <table class="order-table">
                          <thead>
                              <tr>
                                  <th>Item</th>
                                  <th>Qty</th>
                                  <th>Price</th>
                                  <th>Extended</th>
                              </tr>
                          </thead>
                          <tbody>
                              ${orders.map(order => {
                                const attributes = order.attribute ? JSON.parse(order.attribute) : {};
                                const attributeText = Object.entries(attributes).map(([key, value]) => `${key}:${value}`).join(',');
                                return `
                                  <tr>
                                      <td>${order.product_name || 'N/A'} (${attributeText || 'No attributes'})</td>
                                      <td>${order.quantity || "N/A"}</td>
                                      <td>${order.unit_price || "N/A"}</td>
                                      <td>${order.price || "N/A"}</td>
                                  </tr>
                                `;
                              }).join('')}
                          </tbody>
                      </table>
                  </div>
                  
                  <div class="notes-section">
                      <div class="notes-label">Notes:</div>
                      <div class="notes-content">
                          ---
                      </div>
                  </div>
              </div>
              
              <div>
                  ${new Date().toLocaleDateString()} | Page 1 of 1
              </div>
          </div>
      </body>
      </html>
    `;

    const element = document.createElement("div");
    element.innerHTML = htmlContent;

    const opt = {
      margin: 0,
      filename: `Order_${orderId}_${storeName}_${new Date().toISOString().split('T')[0]}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" }
    };

    html2pdf().from(element).set(opt).save();
  };

  const generatePDF = () => {
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Sales Report - ${storeName}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; }
          .summary { display: flex; justify-content: space-around; margin: 20px 0; }
          .summary-card { text-align: center; padding: 15px; border: 1px solid #ddd; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; }
          .total-row { font-weight: bold; background-color: #e6f3ff; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Sales Report</h1>
          <h2>${storeName}</h2>
          <p>Generated on: ${new Date().toLocaleDateString()}</p>
        </div>
        
        <div class="summary">
          <div class="summary-card">
            <h3>Total Orders</h3>
            <p>${totalOrders}</p>
          </div>
          <div class="summary-card">
            <h3>Total Revenue</h3>
            <p>$${totalSales.toFixed(2)}</p>
          </div>
          <div class="summary-card">
            <h3>Completed Orders</h3>
            <p>${metrics.completedOrders}</p>
          </div>
          <div class="summary-card">
            <h3>Avg Order Value</h3>
            <p>$${metrics.averageOrderValue.toFixed(2)}</p>
          </div>
          <div class="summary-card">
            <h3>Total Customers</h3>
            <p>${metrics.totalCustomers}</p>
          </div>
        </div>
        
        <div class="analytics">
          <div class="top-products">
            <h3>Top Products by Revenue</h3>
            <table style="margin-top: 10px; width: 48%; display: inline-table;">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Orders</th>
                  <th>Qty Sold</th>
                  <th>Revenue</th>
                </tr>
              </thead>
              <tbody>
                ${metrics.topProducts.map(product => `
                  <tr>
                    <td>${product.name}</td>
                    <td>${product.orders}</td>
                    <td>${product.quantity}</td>
                    <td>$${product.revenue.toFixed(2)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
          
          <div class="order-status" style="display: inline-block; width: 48%; margin-left: 4%; vertical-align: top;">
            <h3>Payment Status Breakdown</h3>
            <table style="margin-top: 10px;">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                ${Object.entries(metrics.ordersByStatus).map(([status, count]) => `
                  <tr>
                    <td>${status}</td>
                    <td>${count}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Customer</th>
              <th>Product</th>
              <th>Attributes</th>
              <th>Qty</th>
              <th>Unit Price</th>
              <th>Total</th>
              <th>Payment</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            ${orderData.map(order => {
              const attributes = order.attribute ? JSON.parse(order.attribute) : {};
              const attributeText = Object.entries(attributes).map(([key, value]) => `${key}: ${value}`).join(', ');
              return `
                <tr>
                  <td>${order.order_id || 'N/A'}</td>
                  <td>${order.username || 'N/A'}</td>
                  <td>${order.product_name || 'N/A'}</td>
                  <td style="font-size: 10px;">${attributeText || 'N/A'}</td>
                  <td>${order.quantity || 0}</td>
                  <td>$${parseFloat(order.unit_price || 0).toFixed(2)}</td>
                  <td>$${(order.quantity * parseFloat(order.price || order.unit_price || 0)).toFixed(2)}</td>
                  <td>${order.paymentStatus || 'Pending'}</td>
                  <td>${order.orderStatus || 'Pending'}</td>
                  <td>${new Date(order.orderDate || order.createdOn || order.created_at || Date.now()).toLocaleDateString()}</td>
                </tr>
              `;
            }).join('')}
            <tr class="total-row">
              <td colspan="6">Grand Total</td>
              <td>$${totalSales.toFixed(2)}</td>
              <td colspan="3"></td>
            </tr>
          </tbody>
        </table>
      </body>
      </html>
    `;

    const element = document.createElement("div");
    element.innerHTML = htmlContent;

    const opt = {
      margin: 10,
      filename: `Sales_Report_${storeName}_${new Date().toISOString().split('T')[0]}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" }
    };

    html2pdf().from(element).set(opt).save();
  };

  return (
    <>
      <Header />
      <div className="dbAdminRight newDbRight">
        <div className="dbMid dbInner">
          <div className="dbMainHd">
            <button 
              onClick={() => navigate(-1)} 
              className="btn btn-secondary"
              style={{ marginRight: "20px" }}
            >
              ← Back
            </button>
            Sales Report - {storeName}
          </div>

          {/* Filter Section */}
          <div className="catFilterWrp">
            <div className="catFilterCont">
              {/* First Line: Search, Limit, and Status Filters */}
              <div className="filWrp" style={{ display: "flex", alignItems: "center", gap: "15px", marginBottom: "15px" }}>
                <div>
                  <label className="label2">Search:</label>
                  <input
                    type="text"
                    placeholder="Search orders..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="form_ctrl"
                  />
                </div>
                <div>
                  <label className="label2">Limit:</label>
                  <select
                    value={limit}
                    onChange={(e) => setLimit(parseInt(e.target.value))}
                    className="form_ctrl"
                  >
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={500}>500</option>
                    <option value={1000}>1000</option>
                    <option value={5000}>5000</option>
                  </select>
                </div>
                <div>
                  <label className="label2">Payment Status:</label>
                  <select
                    value={paymentStatus}
                    onChange={(e) => setPaymentStatus(e.target.value)}
                    className="form_ctrl"
                  >
                    <option value="">All</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="PENDING">Pending</option>
                    <option value="FAILED">Failed</option>
                    <option value="CANCELLED">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="label2">Order Status:</label>
                  <select
                    value={orderStatus}
                    onChange={(e) => setOrderStatus(e.target.value)}
                    className="form_ctrl"
                  >
                    <option value="">All</option>
                    <option value="Pending">Pending</option>
                    <option value="Processing">Processing</option>
                    <option value="Shipped">Shipped</option>
                    <option value="Delivered">Delivered</option>
                    <option value="Cancelled">Cancelled</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>
              </div>
              
              {/* Second Line: Dates and Buttons */}
              <div className="filWrp" style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                <div>
                  <label className="label2">From Date:</label>
                  <input
                    type="date"
                    value={dateRange.fromDate}
                    onChange={(e) => setDateRange({...dateRange, fromDate: e.target.value})}
                    className="form_ctrl"
                  />
                </div>
                <div>
                  <label className="label2">To Date:</label>
                  <input
                    type="date"
                    value={dateRange.toDate}
                    onChange={(e) => setDateRange({...dateRange, toDate: e.target.value})}
                    className="form_ctrl"
                  />
                </div>
                <button 
                  onClick={handleDateFilter}
                  className="btn btn-primary"
                  disabled={loading}
                >
                  Filter
                </button>
                <button 
                  onClick={clearFilter}
                  className="btn btn-secondary"
                >
                  Clear
                </button>
                <button 
                  onClick={generatePDF}
                  className="btn btn-success"
                  disabled={orderData.length === 0}
                >
                  Download PDF
                </button>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "20px", margin: "20px 0" }}>
            <div className="summary-card" style={{ 
              padding: "20px", 
              backgroundColor: "#f8f9fa", 
              borderRadius: "8px",
              textAlign: "center",
              border: "1px solid #dee2e6"
            }}>
              <h3 style={{ margin: "0 0 10px 0", color: "#495057", fontSize: "16px" }}>Total Orders</h3>
              <p style={{ fontSize: "24px", fontWeight: "bold", margin: 0, color: "#007bff" }}>
                {totalOrders}
              </p>
            </div>
            <div className="summary-card" style={{ 
              padding: "20px", 
              backgroundColor: "#f8f9fa", 
              borderRadius: "8px",
              textAlign: "center",
              border: "1px solid #dee2e6"
            }}>
              <h3 style={{ margin: "0 0 10px 0", color: "#495057", fontSize: "16px" }}>Total Items</h3>
              <p style={{ fontSize: "24px", fontWeight: "bold", margin: 0, color: "#17a2b8" }}>
                {metrics.totalItems}
              </p>
            </div>
            <div className="summary-card" style={{ 
              padding: "20px", 
              backgroundColor: "#f8f9fa", 
              borderRadius: "8px",
              textAlign: "center",
              border: "1px solid #dee2e6"
            }}>
              <h3 style={{ margin: "0 0 10px 0", color: "#495057", fontSize: "16px" }}>Total Revenue</h3>
              <p style={{ fontSize: "24px", fontWeight: "bold", margin: 0, color: "#28a745" }}>
                ${totalSales.toFixed(2)}
              </p>
            </div>
            <div className="summary-card" style={{ 
              padding: "20px", 
              backgroundColor: "#f8f9fa", 
              borderRadius: "8px",
              textAlign: "center",
              border: "1px solid #dee2e6"
            }}>
              <h3 style={{ margin: "0 0 10px 0", color: "#495057", fontSize: "16px" }}>Completed Orders</h3>
              <p style={{ fontSize: "24px", fontWeight: "bold", margin: 0, color: "#17a2b8" }}>
                {metrics.completedOrders}
              </p>
            </div>
            <div className="summary-card" style={{ 
              padding: "20px", 
              backgroundColor: "#f8f9fa", 
              borderRadius: "8px",
              textAlign: "center",
              border: "1px solid #dee2e6"
            }}>
              <h3 style={{ margin: "0 0 10px 0", color: "#495057", fontSize: "16px" }}>Avg Order Value</h3>
              <p style={{ fontSize: "24px", fontWeight: "bold", margin: 0, color: "#6f42c1" }}>
                ${metrics.averageOrderValue.toFixed(2)}
              </p>
            </div>
            <div className="summary-card" style={{ 
              padding: "20px", 
              backgroundColor: "#f8f9fa", 
              borderRadius: "8px",
              textAlign: "center",
              border: "1px solid #dee2e6"
            }}>
              <h3 style={{ margin: "0 0 10px 0", color: "#495057", fontSize: "16px" }}>Total Customers</h3>
              <p style={{ fontSize: "24px", fontWeight: "bold", margin: 0, color: "#fd7e14" }}>
                {metrics.totalCustomers}
              </p>
            </div>
          </div>

          {/* Charts Section */}
          <div className="charts-container" style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", 
            gap: "20px", 
            margin: "20px 0" 
          }}>
            {/* Payment Status Pie Chart */}
            <div className="chart-card" style={{ 
              padding: "20px", 
              backgroundColor: "#fff", 
              borderRadius: "8px",
              border: "1px solid #dee2e6",
              height: "300px",
              minWidth: "300px"
            }}>
              <canvas ref={statusChartRef} style={{ maxHeight: "250px" }}></canvas>
            </div>

            {/* Top Products Bar Chart */}
            <div className="chart-card" style={{ 
              padding: "20px", 
              backgroundColor: "#fff", 
              borderRadius: "8px",
              border: "1px solid #dee2e6",
              height: "300px",
              minWidth: "300px"
            }}>
              <canvas ref={productsChartRef} style={{ maxHeight: "250px" }}></canvas>
            </div>
          </div>

          <style jsx>{`
            @media (max-width: 768px) {
              .charts-container,
              .analytics-container {
                grid-template-columns: 1fr !important;
              }
              .chart-card {
                min-width: 100% !important;
                height: 250px !important;
              }
            }
          `}</style>

          {/* Analytics Section */}
          <div className="analytics-container" style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", 
            gap: "20px", 
            margin: "20px 0" 
          }}>
            {/* Top Products */}
            <div style={{ 
              padding: "20px", 
              backgroundColor: "#fff", 
              borderRadius: "8px",
              border: "1px solid #dee2e6"
            }}>
              <h4 style={{ marginBottom: "15px", color: "#495057" }}>Top Products by Revenue</h4>
              {metrics.topProducts.length > 0 ? (
                <div>
                  {metrics.topProducts.map((product, index) => (
                    <div key={index} style={{ 
                      display: "flex", 
                      justifyContent: "space-between", 
                      padding: "8px 0", 
                      borderBottom: index < metrics.topProducts.length - 1 ? "1px solid #eee" : "none"
                    }}>
                      <span style={{ fontWeight: "500" }}>{product.name}</span>
                      <span style={{ color: "#28a745", fontWeight: "bold" }}>${product.revenue.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: "#6c757d", fontStyle: "italic" }}>No product data available</p>
              )}
            </div>

            {/* Payment Status Breakdown */}
            <div style={{ 
              padding: "20px", 
              backgroundColor: "#fff", 
              borderRadius: "8px",
              border: "1px solid #dee2e6"
            }}>
              <h4 style={{ marginBottom: "15px", color: "#495057" }}>Payment Status Breakdown</h4>
              {Object.keys(metrics.ordersByStatus).length > 0 ? (
                <div>
                  {Object.entries(metrics.ordersByStatus).map(([status, count], index) => (
                    <div key={index} style={{ 
                      display: "flex", 
                      justifyContent: "space-between", 
                      padding: "8px 0", 
                      borderBottom: index < Object.keys(metrics.ordersByStatus).length - 1 ? "1px solid #eee" : "none"
                    }}>
                      <span style={{ fontWeight: "500" }}>{status}</span>
                      <span style={{ 
                        color: status === 'COMPLETED' ? '#28a745' : status === 'PENDING' ? '#ffc107' : status === 'CANCELLED' ? '#dc3545' : '#6c757d',
                        fontWeight: "bold" 
                      }}>{count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: "#6c757d", fontStyle: "italic" }}>No status data available</p>
              )}
            </div>
          </div>

          {/* Orders Accordion */}
          <div className="listTablWrp">
            {loading ? (
              <div style={{ textAlign: "center", padding: "50px" }}>
                <p>Loading orders...</p>
              </div>
            ) : (
              <div className="tblWrp">
                <table>
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Customer</th>
                      <th>Product Name</th>
                      <th>Attributes</th>
                      <th>Quantity</th>
                      <th>Unit Price</th>
                      <th>Total Amount</th>
                      <th>Payment Status</th>
                      <th>Order Status</th>
                      <th>Order Date</th>
                    </tr>
                  </thead>
                </table>
                
                {orderData.length > 0 ? (
                  <Accordion defaultActiveKey="0">
                    {Object.entries(
                      orderData.reduce((acc, order) => {
                        if (!acc[order.order_id]) acc[order.order_id] = [];
                        acc[order.order_id].push(order);
                        return acc;
                      }, {})
                    )
                    .sort(([,a], [,b]) => new Date(b[0].createdOn || b[0].created_at) - new Date(a[0].createdOn || a[0].created_at))
                    .map(([orderId, orders], idx) => {
                      const orderTotal = orders.reduce((sum, order) => 
                        sum + (order.quantity * parseFloat(order.unit_price || 0)), 0
                      );
                      
                      return (
                        <Accordion.Item eventKey={String(idx)} key={orderId}>
                          <Accordion.Header>
                            <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <div>
                                <strong>Order ID:</strong> {orderId} | 
                                <strong> Items:</strong> {orders.length} | 
                                <strong> Total:</strong> ${orderTotal.toFixed(2)} | 
                                <strong> Date:</strong> {new Date(orders[0].createdOn || orders[0].created_at || Date.now()).toLocaleDateString()}
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  generateOrderPDF(orderId, orders, storeName);
                                }}
                                className="btn btn-sm btn-outline-primary"
                                style={{
                                  padding: "4px 8px",
                                  fontSize: "12px",
                                  borderRadius: "4px",
                                  marginLeft: "10px"
                                }}
                                title="Download PDF Report"
                              >
                                📄 PDF
                              </button>
                            </div>
                          </Accordion.Header>
                          <Accordion.Body>
                            <table>
                              <tbody>
                                {orders.map((order, index) => {
                                  const attributes = order.attribute ? JSON.parse(order.attribute) : {};
                                  const attributeText = Object.entries(attributes).map(([key, value]) => `${key}: ${value}`).join(', ');
                                  return (
                                    <tr key={index}>
                                      <td>{orderId}</td>
                                      <td>{order.username || 'N/A'}</td>
                                      <td>{order.product_name || 'N/A'}</td>
                                      <td style={{ fontSize: "12px", color: "#6c757d" }}>{attributeText || 'N/A'}</td>
                                      <td>{order.quantity || 0}</td>
                                      <td>${parseFloat(order.unit_price || 0).toFixed(2)}</td>
                                      <td>${(parseFloat(order.price || order.unit_price || 0)).toFixed(2)}</td>
                                      <td>
                                        <span style={{
                                          padding: "4px 8px",
                                          borderRadius: "4px",
                                          fontSize: "12px",
                                          fontWeight: "bold",
                                          backgroundColor: order.paymentStatus === 'COMPLETED' ? '#d4edda' : '#fff3cd',
                                          color: order.paymentStatus === 'COMPLETED' ? '#155724' : '#856404'
                                        }}>
                                          {order.paymentStatus || 'Pending'}
                                        </span>
                                      </td>
                                      <td>
                                        <span style={{
                                          padding: "4px 8px",
                                          borderRadius: "4px",
                                          fontSize: "12px",
                                          fontWeight: "bold",
                                          backgroundColor: order.orderStatus === 'Completed' ? '#d4edda' : '#f8d7da',
                                          color: order.orderStatus === 'Completed' ? '#155724' : '#721c24'
                                        }}>
                                          {order.orderStatus || 'Pending'}
                                        </span>
                                      </td>
                                      <td>{new Date(order.orderDate || order.createdOn || order.created_at || Date.now()).toLocaleDateString()}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </Accordion.Body>
                        </Accordion.Item>
                      );
                    })}
                  </Accordion>
                ) : (
                  <div style={{ textAlign: "center", padding: "30px" }}>
                    No orders found for this store
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SalesReport;